import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { TinyHouseModel } from '@/types'

import Head from 'next/head'
import Link from 'next/link'
import { ArrowRight, Check, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CmsImage } from '@/components/generals/cms-image'
import { Eyebrow, SectionHeading } from '@/components/generals/section-heading'
import RichText from '@/components/generals/rich-text'
import { ModelCard } from '@/components/landing/model-card'
import { OptionsGrid } from '@/components/landing/options-grid'
import { CtaBanner } from '@/components/landing/cta-banner'
import { buttonVariants } from '@/components/ui/button'
import { getModelBySlug, getModels, getModelSlugs } from '@/lib/content'
import { INCLUDED } from '@/constants/content'
import { siteConfig } from '@/constants/site'

interface ModelPageProps {
    model: TinyHouseModel
    otherModels: TinyHouseModel[]
}

type SpecField = 'price' | 'length' | 'width' | 'height' | 'floor_area' | 'sleeps' | 'weight' | 'build_time'

const KEY_SPECS: { field: SpecField, label: string }[] = [
    { field: 'length', label: "Length" },
    { field: 'floor_area', label: "Floor area" },
    { field: 'sleeps', label: "Sleeps" },
    { field: 'weight', label: "Weight" },
    { field: 'height', label: "Height" },
    { field: 'build_time', label: "Build time" }
]

const ALL_SPECS: { field: SpecField, label: string }[] = [
    { field: 'price', label: "Price from" },
    { field: 'length', label: "Length" },
    { field: 'width', label: "Width" },
    { field: 'height', label: "Height" },
    { field: 'floor_area', label: "Floor area" },
    { field: 'sleeps', label: "Sleeps" },
    { field: 'weight', label: "Weight" },
    { field: 'build_time', label: "Build time in the workshop" }
]

const toProductJsonLd = (model: TinyHouseModel): string => {
    const data = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: model.name,
        description: model.summary ?? undefined,
        image: model.cover_image ?? undefined,
        brand: {
            '@type': 'Brand',
            name: siteConfig.name
        }
    }

    return JSON.stringify(data).replace(/</g, '\\u003c')
}

const ModelPage: FC<ModelPageProps> = ({ model, otherModels }): ReactNode => {
    const keySpecs = KEY_SPECS.filter(spec => model[spec.field])
    const allSpecs = ALL_SPECS.filter(spec => model[spec.field])
    const features = model.features.length > 0 ? model.features : INCLUDED
    const quoteHref = `/contact?model=${model.slug}`

    return (
        <Layout>
            <Seo
                title={`${model.name} tiny house`}
                description={model.summary ?? undefined}
                ogImage={model.cover_image ?? undefined}
            />

            <Head>
                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: toProductJsonLd(model) }}
                />
            </Head>

            <section id='model-overview' className='pt-6 pb-16 lg:pt-10 lg:pb-24'>
                <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                    <nav aria-label="Breadcrumb">
                        <ol className='flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                            <li>
                                <Link href='/' className='transition-colors hover:text-foreground'>
                                    Home
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-4' />
                            </li>

                            <li>
                                <Link href='/models' className='transition-colors hover:text-foreground'>
                                    Models
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-4' />
                            </li>

                            <li aria-current='page' className='font-medium text-foreground'>
                                <span data-mdlfy-cms={`models:${model.id}:name`}>{model.name}</span>
                            </li>
                        </ol>
                    </nav>

                    <div className='mt-8 grid gap-10 lg:grid-cols-12 lg:gap-14'>
                        <div className='lg:col-span-7'>
                            <div className='relative aspect-[4/3] overflow-hidden rounded-[2rem] bg-secondary'>
                                <CmsImage
                                    src={model.cover_image}
                                    alt={model.cover_image_alt || `${model.name} tiny house`}
                                    priority
                                />
                            </div>
                        </div>

                        <div className='animate-in fade-in slide-in-from-bottom-3 duration-700 motion-reduce:animate-none lg:col-span-5 lg:pt-2'>
                            {model.best_for && (
                                <p className='inline-flex flex-wrap items-center gap-1.5 rounded-full bg-secondary px-3.5 py-1.5 text-xs font-semibold'>
                                    <span className='text-muted-foreground'>Best for</span>
                                    <span data-mdlfy-cms={`models:${model.id}:best_for`}>{model.best_for}</span>
                                </p>
                            )}

                            <h1 className='mt-5 font-heading text-6xl leading-none font-medium tracking-tight sm:text-7xl'>
                                <span data-mdlfy-cms={`models:${model.id}:name`}>{model.name}</span>
                            </h1>

                            {model.tagline && (
                                <p className='mt-4 font-heading text-2xl text-timber italic' data-mdlfy-cms={`models:${model.id}:tagline`}>{model.tagline}</p>
                            )}

                            {model.summary && (
                                <p className='mt-6 text-lg leading-relaxed text-muted-foreground' data-mdlfy-cms={`models:${model.id}:summary`}>{model.summary}</p>
                            )}

                            {keySpecs.length > 0 && (
                                <dl className='mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-3'>
                                    {keySpecs.map(spec => (
                                        <div key={spec.field} className='bg-card px-4 py-4'>
                                            <dt className='text-xs text-muted-foreground'>
                                                {spec.label}
                                            </dt>

                                            <dd className='mt-1 font-semibold'>
                                                <span data-mdlfy-cms={`models:${model.id}:${spec.field}`}>{model[spec.field]}</span>
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            )}

                            <div className='mt-8 flex flex-wrap items-end gap-x-8 gap-y-5'>
                                {model.price && (
                                    <div>
                                        <p className='text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase'>
                                            From
                                        </p>

                                        <p className='mt-1 font-heading text-4xl leading-none font-medium' data-mdlfy-cms={`models:${model.id}:price`}>{model.price}</p>
                                    </div>
                                )}

                                <Link href={quoteHref} className={buttonVariants({ size: 'lg' })}>
                                    Request a quote
                                    <ArrowRight className='size-4' />
                                </Link>
                            </div>

                            <p className='mt-4 text-sm text-muted-foreground'>
                                Starting price before options, delivery and site connections.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section id='about-this-model' className='border-t border-border/70 py-16 lg:py-24'>
                <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16'>
                    <div className='lg:col-span-7'>
                        <Eyebrow>
                            About this model
                        </Eyebrow>

                        <h2 className='mt-4 font-heading text-4xl leading-tight font-medium tracking-tight sm:text-5xl'>
                            Designed around everyday life
                        </h2>

                        {model.description && (
                            <RichText html={model.description} className='mt-8 text-lg leading-relaxed text-muted-foreground' />
                        )}
                    </div>

                    <aside className='lg:col-span-5'>
                        <div className='rounded-2xl border border-border bg-card p-7 sm:p-8 lg:sticky lg:top-28'>
                            <h3 className='font-heading text-2xl font-medium'>
                                {model.features.length > 0 ? "Highlights" : "Included as standard"}
                            </h3>

                            <ul className='mt-6 space-y-4'>
                                {features.map((feature, index) => (
                                    <li key={`${index}-${feature}`} className='flex items-start gap-3'>
                                        <span className='mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'>
                                            <Check className='size-3.5' aria-hidden='true' />
                                        </span>

                                        <span className='leading-relaxed'>
                                            {feature}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <Link href={quoteHref} className={cn(buttonVariants({ variant: 'outline' }), 'mt-8 w-full')}>
                                Ask about this model
                            </Link>
                        </div>
                    </aside>
                </div>
            </section>

            {model.interior_image && (
                <section id='interior' aria-label="Interior" className='px-5 sm:px-8'>
                    <div className='relative mx-auto aspect-[4/3] max-w-7xl overflow-hidden rounded-[2rem] bg-secondary sm:aspect-[16/9]'>
                        <CmsImage
                            src={model.interior_image}
                            alt={model.interior_image_alt || `Inside the ${model.name} tiny house`}
                        />
                    </div>
                </section>
            )}

            {model.gallery.length > 0 && (
                <section id='gallery' className='pt-16 lg:pt-24'>
                    <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                        <h2 className='font-heading text-3xl font-medium tracking-tight sm:text-4xl'>
                            Gallery
                        </h2>

                        <div className='mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                            {model.gallery.map((src, index) => (
                                <div key={`${index}-${src}`} className='relative aspect-[4/3] overflow-hidden rounded-2xl bg-secondary'>
                                    <CmsImage
                                        src={src}
                                        alt={model.gallery_alt[index] || `${model.name} tiny house, photo ${index + 1}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {allSpecs.length > 0 && (
                <section id='specifications' className='py-16 lg:py-24'>
                    <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16'>
                        <div className='lg:col-span-5'>
                            <SectionHeading
                                eyebrow="Specifications"
                                title="The numbers"
                                description="Built on a certified trailer and sized to stay within road limits, so your home can move when you do."
                            />
                        </div>

                        <dl className='divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card lg:col-span-7'>
                            {allSpecs.map(spec => (
                                <div key={spec.field} className='flex items-center justify-between gap-6 px-6 py-4'>
                                    <dt className='text-muted-foreground'>
                                        {spec.label}
                                    </dt>

                                    <dd className='text-right font-semibold'>
                                        <span data-mdlfy-cms={`models:${model.id}:${spec.field}`}>{model[spec.field]}</span>
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </section>
            )}

            <section id='make-it-yours' className='border-y border-border/70 bg-secondary/50 py-16 lg:py-24'>
                <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                    <SectionHeading
                        eyebrow="Make it yours"
                        title="Options and upgrades"
                        description="Every model can be adapted. These are the upgrades our clients ask for most, each priced individually in your quote."
                    />

                    <OptionsGrid className='mt-12' />
                </div>
            </section>

            {otherModels.length > 0 && (
                <section id='other-models' className='py-16 lg:py-24'>
                    <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                        <div className='flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between'>
                            <SectionHeading
                                eyebrow="Keep exploring"
                                title="Other models"
                            />

                            <Link href='/models' className={cn(buttonVariants({ variant: 'outline' }), 'self-start sm:self-auto')}>
                                All models
                                <ArrowRight className='size-4' />
                            </Link>
                        </div>

                        <div className='mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
                            {otherModels.map(other => (
                                <ModelCard key={other.id} model={other} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <CtaBanner
                title="Like what you see?"
                description="Request a quote for this model and we will send a detailed price with the options you choose, with no obligation."
                primaryHref={quoteHref}
                secondaryHref='/models#compare'
                secondaryLabel="Compare models"
            />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getModelSlugs()

        return {
            paths: slugs.map(slug => ({ params: { slug } })),
            fallback: 'blocking'
        }
    }
    catch (error) {
        console.error('Model pages could not be listed:', error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<ModelPageProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true, revalidate: 60 }

    const [model, models] = await Promise.all([
        getModelBySlug(slug),
        getModels()
    ])

    if (!model) return { notFound: true, revalidate: 60 }

    return {
        props: {
            model,
            otherModels: models.filter(other => other.id !== model.id)
        },
        revalidate: 60
    }
}

export default ModelPage
